import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Collapse, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { ArrowDropDown, ExpandMore, LocationOn } from '@mui/icons-material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { RoutineActivityRecurring } from '@demind-inc/core';

import { ChatSuggestedEvent, SESSION_EMOJI, useCalendarContext } from '../../../data-access';
import { getSuggestedBlockKey } from '../../../data-access/helpers';
import type { NearbyPlace } from '../../../data-access/api/science/openapi/api';
import type { UnifiedChatRichContextEntry } from '../../../data-access/types';
import { AssistantChatRichContextCard } from '../AssistantChatRichContextCard';
import {
  formatTimeFromDayjs,
  findCTAType,
  formatTimeFromHHMM,
  isHHMMFormat,
} from '../../../helpers';
import { useGeneralSettings } from '../../../hooks';
import './AssistantChatSuggestedBlocks.scss';
import { AssistantChatSuggestedBlockCTA } from '../AssistantChatSuggestedBlockCTA';
import { getDisplayDateLabel } from './helpers';
import { PendingIndicatorDot } from '../../common';
import { EventRecurrenceSelectDropdown } from '../../CalendarView/EventMenu/EventRecurrenceSelectDropdown';
import { CalendarAccountDropdown } from '../../CalendarView/EventMenu/CalendarAccountDropdown';
import { useTranslation } from 'react-i18next';
import RoutineRecurringSelector from '../../Scheduler/common/RoutineRecurringSelector/RoutineRecurringSelector';
import { toRoutineActivityRecurring } from '../../Scheduler/common/recurrence.helpers';
import { SuggestedBlockHandle, SuggestedBlockActionsVisibility } from '../SuggestedBlocks/types';
import SuggestedBlockActionsRow from '../SuggestedBlocks/SuggestedBlockActionsRow';
import { getSuggestedBlockPalette } from './useSuggestedBlockPalette';
import { DescriptionBox } from '../DescriptionBox';
import SleepPlanResultBg from '../../../assets/calendar/suggestions/sleep_block_bg.png';
import MealPlanResultBg from '../../../assets/calendar/suggestions/meal_block_bg.png';

/** Title emoji for a plan result that came from a task or routine. Matches
 * lifestack_calendar's getSuggestedEventEmoji (todo → SESSION_EMOJI.task). */
const getSuggestedEventEmoji = (from?: string): string | undefined => {
  switch (from) {
    case 'todo':
      return SESSION_EMOJI.task;
    case 'routine':
      return SESSION_EMOJI.routine;
    default:
      return undefined;
  }
};

/** Sleep/meal names are often stored as "😴 Sleep". Drop a leading copy of the
 * same emoji this card renders separately, so it isn't shown twice. */
const stripLeadingEmojiPrefix = (name: string, emoji: string): string => {
  let rest = name.trimStart();
  if (!rest.startsWith(emoji)) {
    return name;
  }
  while (rest.startsWith(emoji)) {
    rest = rest.slice(emoji.length).trimStart();
  }
  return rest;
};

interface AssistantChatSuggestedEventBlockProps {
  block: ChatSuggestedEvent;
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageCalendar?: (e: ChatSuggestedEvent) => Promise<boolean>;
  onCheckCalendar?: (startTime: string) => void;
  nearbyPlaces?: NearbyPlace[];
  /** 'adjustment'-sourced richContext entries matched to this block from the
   * message level (see buildRichContextEntriesByEventId) — rendered
   * alongside block.richContext inside this card. */
  richContextEntries?: UnifiedChatRichContextEntry[];
  /** 'screen' renders the grouped-card action row (Accept/Discard) instead
   * of the plain chat-feed CTA — used by SuggestedBlockRow/SuggestionGroupCard. */
  variant?: 'default' | 'screen';
  onDiscard?: (eventId: string) => void;
  actionsVisibility?: SuggestedBlockActionsVisibility;
  defaultExpanded?: boolean;
}

const AssistantChatSuggestedEventBlock = memo(
  forwardRef<SuggestedBlockHandle, AssistantChatSuggestedEventBlockProps>(
    (
      {
        block,
        loadingEventId,
        processedIds,
        onProcessedIdsChange,
        onManageCalendar = () => Promise.resolve(false),
        onCheckCalendar,
        nearbyPlaces = [],
        richContextEntries: matchedRichContextEntries = [],
        variant = 'default',
        onDiscard,
        actionsVisibility = 'visible',
        defaultExpanded = true,
      },
      ref
    ) => {
      const { t } = useTranslation();
      const {
        generalSettings: { timeFormat },
      } = useGeneralSettings();
      const formatBlockTime = (time: string) =>
        isHHMMFormat(time)
          ? formatTimeFromHHMM(time, timeFormat, true)
          : formatTimeFromDayjs(dayjs(time), timeFormat, true);
      const { mainCalendar, calendarAccountsToCreateEvents } = useCalendarContext();
      const ctaType = findCTAType(block);
      const isScreenVariant = variant === 'screen';
      const palette = useMemo(
        () => getSuggestedBlockPalette('event', isScreenVariant),
        [isScreenVariant]
      );
      const blockContainerStyle = {
        '--block-card-bg': palette.cardBackground,
        '--block-chip-bg': palette.chipBackground,
        '--block-time-bg': palette.timePillBackground,
        '--block-secondary-text': palette.secondaryText,
      } as React.CSSProperties;

      const currentDateLabel = getDisplayDateLabel(block.date, block.startTime);
      const beforeDateLabel = getDisplayDateLabel(block.before?.date, block.before?.startTime);

      const buildEventManageTitle = (block: ChatSuggestedEvent) => {
        if (block.action === 'checkOnCalendar') {
          return t('task.ai.suggestedBlocks.checkOnCalendar');
        }

        if (block.action === 'deleteEvent') {
          if (ctaType === 'meal') {
            return t('task.ai.suggestedBlocks.deleteMeal');
          } else if (ctaType === 'sleep') {
            return t('task.ai.suggestedBlocks.deleteSleep');
          } else if (!block.before) {
            // No `before` means this targets a still-pending suggestion (never
            // committed to the calendar), not a real event — "Remove" reads
            // more accurately than "Delete" for dropping a suggestion.
            return t('task.ai.suggestedBlocks.removeEvent');
          } else {
            return t('task.ai.suggestedBlocks.deleteEvent');
          }
        }

        if (block.action === 'deleteRoutine') {
          return t('task.ai.suggestedBlocks.deleteRoutine');
        }

        if (block.action === 'setRoutine') {
          return block.before
            ? t('task.ai.suggestedBlocks.updateRoutine')
            : t('task.ai.suggestedBlocks.createRoutine');
        }

        if (block.action === 'setEvent') {
          if (ctaType === 'meal') {
            return block.before
              ? t('task.ai.suggestedBlocks.updateMeal')
              : t('task.ai.suggestedBlocks.createMeal');
          } else if (ctaType === 'sleep') {
            return block.before
              ? t('task.ai.suggestedBlocks.updateSleep')
              : t('task.ai.suggestedBlocks.createSleep');
          } else {
            return block.before
              ? t('task.ai.suggestedBlocks.updateEvent')
              : t('task.ai.suggestedBlocks.createEvent');
          }
        }
        return block.before
          ? t('task.ai.suggestedBlocks.updateEvent')
          : t('task.ai.suggestedBlocks.createEvent');
      };

      const handleClickEventBlock = async (
        block: ChatSuggestedEvent,
        recurrence: string | null = null
      ): Promise<boolean> => {
        if (block.action === 'checkOnCalendar') {
          onCheckCalendar?.(block.startTime);
          return false;
        }
        if (
          block.action === 'setEvent' ||
          block.action === 'setRoutine' ||
          block.action === 'deleteEvent' ||
          block.action === 'deleteRoutine'
        ) {
          let eventToSave: ChatSuggestedEvent = { ...block };
          if (recurrence && block.action === 'setEvent' && ctaType === 'calendar') {
            eventToSave = { ...eventToSave, recurrence: { string: recurrence } };
          }
          if (block.action === 'setRoutine') {
            eventToSave = { ...eventToSave, recurring: selectedRoutineRecurring };
          }
          if (shouldShowLocationSelector) {
            eventToSave = { ...eventToSave, location: selectedLocation || undefined };
          }
          const result = await onManageCalendar(eventToSave);
          if (result) {
            onProcessedIdsChange([...processedIds, getSuggestedBlockKey(block)]);
          }
          return result;
        }
        return false;
      };

      const isDeleteAction = block.action === 'deleteEvent' || block.action === 'deleteRoutine';

      const blockKey = getSuggestedBlockKey(block);
      const isProcessed = processedIds.includes(blockKey);
      const isLoading = loadingEventId === blockKey;
      const [selectedRecurrence, setSelectedRecurrence] = useState<string | null>(
        block.recurrence?.string ?? null
      );
      const [selectedRoutineRecurring, setSelectedRoutineRecurring] =
        useState<RoutineActivityRecurring>(() => toRoutineActivityRecurring(block.recurring));
      const [selectedCalendarId, setSelectedCalendarId] = useState<string>(
        block.calendarId || mainCalendar?.calendarId || ''
      );
      const [selectedLocation, setSelectedLocation] = useState<string>(block.location ?? '');
      const [isReasonExpanded, setIsReasonExpanded] = useState(defaultExpanded);
      // Screen variant (SuggestionGroupCard/PlanPreviewBubble) starts each card
      // collapsed to just its header — matches RN's isExpanded/handleToggleExpanded;
      // the default (plain chat-feed) variant ignores this and always shows the
      // full body, since isScreenVariant gates every usage below.
      const [isExpanded, setIsExpanded] = useState(defaultExpanded);
      const [locationMenuWidth, setLocationMenuWidth] = useState<number>();
      const locationSelectorRef = useRef<HTMLDivElement>(null);
      const shouldShowRecurrenceSelector =
        block.action === 'setEvent' && ctaType === 'calendar' && !block.before;
      const shouldShowCalendarSelector =
        block.action === 'setEvent' &&
        ctaType === 'calendar' &&
        !block.before &&
        calendarAccountsToCreateEvents.length > 1;
      const shouldShowLocationSelector = block.action === 'setEvent' && nearbyPlaces.length > 0;
      const shouldShowRoutineRecurringSelector = block.action === 'setRoutine' && !isDeleteAction;

      useImperativeHandle(
        ref,
        () => ({
          accept: () =>
            handleClickEventBlock(
              shouldShowCalendarSelector ? { ...block, calendarId: selectedCalendarId } : block,
              selectedRecurrence
            ),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [block, shouldShowCalendarSelector, selectedCalendarId, selectedRecurrence]
      );

      const isMealBlock = ctaType === 'meal';
      const mealContent = useMemo(
        () => (isMealBlock ? block.mealContent : null),
        [block, isMealBlock]
      );
      const mealTitle = useMemo(
        () =>
          typeof (mealContent as any)?.title === 'string'
            ? String((mealContent as any).title).trim()
            : '',
        [mealContent]
      );
      const mealItems = useMemo(() => {
        const items = (mealContent as any)?.foodItems;
        return Array.isArray(items) ? (items as Array<{ name?: unknown }>) : [];
      }, [mealContent]);
      const macroFocus = useMemo(() => {
        const macros = (mealContent as any)?.macros;
        if (!macros || typeof macros !== 'object') return [];
        return Object.entries(macros as Record<string, unknown>).filter(
          ([, value]) => typeof value === 'number' && value > 0
        );
      }, [mealContent]);
      const hasMealContent = Boolean(mealContent) && (Boolean(mealTitle) || mealItems.length > 0);
      // Combines block.richContext (sleep summary, workout plan, etc. — mainly
      // populated for "Plan my day"/"Replan my day" results) with any
      // message-level 'adjustment' richContext entries matched to this block
      // (see buildRichContextEntriesByEventId) into one list for
      // AssistantChatRichContextCard. `source: 'adjustment'` suppresses the
      // standalone workout "Add to calendar" CTA (this block's own
      // Accept/Discard row already covers that); the card itself renders null
      // when there's nothing to show.
      const richContextEntries = useMemo<UnifiedChatRichContextEntry[]>(() => {
        const ownEntry: UnifiedChatRichContextEntry[] = block.richContext
          ? [
              {
                date: block.date ?? dayjs(block.startTime).format('YYYY-MM-DD'),
                suggestionId: block.eventId,
                source: 'adjustment',
                richContext: block.richContext,
              },
            ]
          : [];
        return [...ownEntry, ...matchedRichContextEntries];
      }, [
        block.richContext,
        block.date,
        block.startTime,
        block.eventId,
        matchedRichContextEntries,
      ]);
      const hasRichContextEntries = richContextEntries.length > 0;
      const isSleepBlock = ctaType === 'sleep';
      // Any meal/sleep adjustment (Plan-my-day/Replan-my-day, single result_json
      // meal/sleep adjustment, or legacy CTA) gets sleep_block_bg/meal_block_bg
      // covering the whole card — matches RN showPlanResultBg.
      const showPlanResultBg = isMealBlock || isSleepBlock;
      const showCalendarColorBar = isScreenVariant && !isDeleteAction;
      const calendarColor = useMemo(
        () =>
          calendarAccountsToCreateEvents.find((cal) => cal.calendarId === selectedCalendarId)
            ?.color ??
          mainCalendar?.color ??
          'var(--color-primary, #680ddb)',
        [calendarAccountsToCreateEvents, selectedCalendarId, mainCalendar?.color]
      );
      // Pending = CTA still awaiting a decision (accept/discard row visible, or
      // accept in flight) and not yet processed — drives the pulsing purple
      // dot on the top-right of the block header.
      const isPending =
        !isProcessed && (actionsVisibility === 'visible' || actionsVisibility === 'loading');
      // Plan-my-day already stamps autoScheduleFrom.from. Plan-my-week keeps
      // that field as aiScheduler and passes the origin via originKind.
      const titleEmoji = getSuggestedEventEmoji(
        block.autoScheduleFrom?.from === 'todo' || block.autoScheduleFrom?.from === 'routine'
          ? block.autoScheduleFrom.from
          : block.originKind
      );
      const displayName = titleEmoji ? stripLeadingEmojiPrefix(block.name, titleEmoji) : block.name;

      useEffect(() => {
        if (block.calendarId) {
          setSelectedCalendarId(block.calendarId);
          return;
        }

        if (!selectedCalendarId && mainCalendar?.calendarId) {
          setSelectedCalendarId(mainCalendar.calendarId);
        }
      }, [block.calendarId, mainCalendar?.calendarId, selectedCalendarId]);

      return (
        <div
          className={clsx('assistant-chat-suggested-blocks__block-container', {
            'assistant-chat-suggested-blocks__block-container--screen': isScreenVariant,
          })}
          style={blockContainerStyle}
        >
          {showPlanResultBg && (
            <img
              src={isSleepBlock ? SleepPlanResultBg : MealPlanResultBg}
              alt=""
              className="assistant-chat-suggested-blocks__plan-result-bg"
            />
          )}
          {showCalendarColorBar && (
            <div
              className="assistant-chat-suggested-blocks__calendar-color-bar"
              style={{ backgroundColor: calendarColor }}
            />
          )}
          <PendingIndicatorDot visible={isPending} style={{ top: 8, right: 8 }} />
          <div
            className={clsx('assistant-chat-suggested-blocks__block-row', {
              'assistant-chat-suggested-blocks__block-row--screen': isScreenVariant,
            })}
            onClick={isScreenVariant ? () => setIsExpanded((prev) => !prev) : undefined}
          >
            <div className="assistant-chat-suggested-blocks__before-wrapper assistant-chat-suggested-blocks__before-wrapper--flex">
              {block.before?.name && block.before.name !== block.name && (
                <Typography
                  variant="body2"
                  className="assistant-chat-suggested-blocks__title assistant-chat-suggested-blocks__before-text"
                >
                  {block.before.name}
                </Typography>
              )}
              <div className="assistant-chat-suggested-blocks__title-with-category">
                {!!titleEmoji && (
                  <span className="assistant-chat-suggested-blocks__title-emoji" aria-hidden>
                    {titleEmoji}
                  </span>
                )}
                <Typography
                  variant="body2"
                  className="assistant-chat-suggested-blocks__title"
                  title={displayName}
                >
                  {displayName}
                </Typography>
              </div>
            </div>
            <div className="assistant-chat-suggested-blocks__datetime-wrapper">
              {isScreenVariant ? (
                <>
                  {!isDeleteAction && (
                    <div className="assistant-chat-suggested-blocks__simple-time-pill">
                      {!!currentDateLabel && currentDateLabel !== 'Today' && (
                        <Typography
                          variant="caption"
                          className="assistant-chat-suggested-blocks__simple-time-date"
                        >
                          {currentDateLabel}
                        </Typography>
                      )}
                      <Typography
                        variant="subtitle2"
                        className="assistant-chat-suggested-blocks__simple-time"
                      >
                        {formatBlockTime(block.startTime)}
                        {' – '}
                        {formatBlockTime(block.endTime)}
                      </Typography>
                    </div>
                  )}
                  <button
                    type="button"
                    className={clsx('assistant-chat-suggested-blocks__expand-toggle', {
                      'assistant-chat-suggested-blocks__expand-toggle--expanded': isExpanded,
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded((prev) => !prev);
                    }}
                    aria-label={
                      isExpanded
                        ? t('task.ai.suggestedBlocks.collapse')
                        : t('task.ai.suggestedBlocks.expand')
                    }
                  >
                    <ExpandMore fontSize="small" />
                  </button>
                </>
              ) : (
                <>
                  {block.action !== 'setRoutine' && (
                    <div
                      className={clsx(
                        'assistant-chat-suggested-blocks__time-item',
                        'assistant-chat-suggested-blocks__time-item--with-purple'
                      )}
                    >
                      {(beforeDateLabel || currentDateLabel) && (
                        <div className={'assistant-chat-suggested-blocks__date-row'}>
                          {beforeDateLabel && beforeDateLabel !== currentDateLabel && (
                            <Typography
                              variant="caption"
                              className="assistant-chat-suggested-blocks__date-chip assistant-chat-suggested-blocks__date-chip--before assistant-chat-suggested-blocks__before-text"
                            >
                              {beforeDateLabel}
                            </Typography>
                          )}
                          {currentDateLabel && (
                            <Typography
                              variant="caption"
                              className="assistant-chat-suggested-blocks__date-chip"
                            >
                              {currentDateLabel}
                            </Typography>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="assistant-chat-suggested-blocks__time-wrapper">
                    <div className="assistant-chat-suggested-blocks__time-item">
                      <div className="assistant-chat-suggested-blocks__before-wrapper">
                        {block.before?.startTime && block.before.startTime !== block.startTime && (
                          <Typography
                            variant="subtitle2"
                            className="assistant-chat-suggested-blocks__time assistant-chat-suggested-blocks__before-text"
                          >
                            {formatBlockTime(block.before.startTime)}
                          </Typography>
                        )}
                        {!isDeleteAction && (
                          <Typography
                            variant="subtitle2"
                            className="assistant-chat-suggested-blocks__time"
                          >
                            {formatBlockTime(block.startTime)}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <Typography variant="subtitle2">:</Typography>
                    <div className="assistant-chat-suggested-blocks__time-item">
                      <div className="assistant-chat-suggested-blocks__before-wrapper">
                        {block.before?.endTime && block.before.endTime !== block.endTime && (
                          <Typography
                            variant="subtitle2"
                            className="assistant-chat-suggested-blocks__time assistant-chat-suggested-blocks__before-text"
                          >
                            {formatBlockTime(block.before.endTime)}
                          </Typography>
                        )}
                        {!isDeleteAction && (
                          <Typography
                            variant="subtitle2"
                            className="assistant-chat-suggested-blocks__time"
                          >
                            {formatBlockTime(block.endTime)}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                  {shouldShowRecurrenceSelector && (
                    <EventRecurrenceSelectDropdown
                      className="assistant-chat-suggested-blocks__recurrence-trigger"
                      iconOnly
                      value={selectedRecurrence}
                      onChange={setSelectedRecurrence}
                      startDate={dayjs(block.startTime)}
                      disabled={isLoading || isProcessed}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <Collapse in={!isScreenVariant || isExpanded} timeout="auto" unmountOnExit>
            <>
              {shouldShowRoutineRecurringSelector && (
                <div className="assistant-chat-suggested-blocks__event-options">
                  <RoutineRecurringSelector
                    className="assistant-chat-suggested-blocks__routine-recurring-selector"
                    recurring={selectedRoutineRecurring}
                    selectedDate={dayjs(block.startTime)}
                    hasSmallContainer
                    disabled={isLoading || isProcessed}
                    onDaySelect={(day) =>
                      setSelectedRoutineRecurring((prev) => ({
                        ...prev,
                        [day]: { enabled: !prev[day]?.enabled },
                      }))
                    }
                  />
                </div>
              )}

              {(shouldShowLocationSelector || !!block.location || shouldShowCalendarSelector) && (
                <div className="assistant-chat-suggested-blocks__event-options">
                  {shouldShowLocationSelector ? (
                    <div
                      ref={locationSelectorRef}
                      className="assistant-chat-suggested-blocks__location-selector"
                    >
                      <Select
                        className="assistant-chat-suggested-blocks__location-selector-select"
                        value={selectedLocation}
                        onChange={(e: SelectChangeEvent<string>) =>
                          setSelectedLocation(e.target.value)
                        }
                        displayEmpty
                        disabled={isLoading || isProcessed}
                        size="small"
                        variant="standard"
                        disableUnderline
                        IconComponent={ArrowDropDown}
                        onOpen={() => {
                          if (locationSelectorRef.current) {
                            setLocationMenuWidth(locationSelectorRef.current.offsetWidth);
                          }
                        }}
                        renderValue={(value) => (
                          <span className="assistant-chat-suggested-blocks__location-selector-value-wrap">
                            <LocationOn
                              className="assistant-chat-suggested-blocks__location-selector-pin"
                              fontSize="small"
                            />
                            <span
                              className={clsx(
                                'assistant-chat-suggested-blocks__location-selector-value',
                                !value &&
                                  'assistant-chat-suggested-blocks__location-selector-value--empty'
                              )}
                            >
                              {value || t('task.ai.suggestedBlocks.noLocation')}
                            </span>
                          </span>
                        )}
                        MenuProps={{
                          PaperProps: {
                            className: 'assistant-chat-suggested-blocks__location-selector-menu',
                            style: locationMenuWidth ? { minWidth: locationMenuWidth } : undefined,
                          },
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                        }}
                      >
                        <MenuItem
                          value=""
                          className="assistant-chat-suggested-blocks__location-selector-menu-item assistant-chat-suggested-blocks__location-selector-menu-item--empty"
                        >
                          <em>{t('task.ai.suggestedBlocks.noLocation')}</em>
                        </MenuItem>
                        {nearbyPlaces.map((place) => (
                          <MenuItem
                            key={place.place_id ?? place.name}
                            value={place.name}
                            className="assistant-chat-suggested-blocks__location-selector-menu-item"
                          >
                            {place.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  ) : (
                    !!block.location && (
                      <div className="assistant-chat-suggested-blocks__location-row">
                        <span className="assistant-chat-suggested-blocks__location-icon">📍</span>
                        <span
                          className="assistant-chat-suggested-blocks__location-label"
                          title={block.location}
                        >
                          {block.location}
                        </span>
                      </div>
                    )
                  )}

                  {shouldShowCalendarSelector && (
                    <div className="assistant-chat-suggested-blocks__calendar-selector">
                      <CalendarAccountDropdown
                        calendars={calendarAccountsToCreateEvents}
                        onSelectCalendarId={setSelectedCalendarId}
                        selectedCalendarId={selectedCalendarId}
                        className="assistant-chat-suggested-blocks__calendar-selector-dropdown"
                        menuPaperClassName="assistant-chat-suggested-blocks__calendar-selector-menu"
                        disabled={isLoading || isProcessed}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Prefer the rich-context Meal Suggestion card when both are present
           (Plan My Day / replan attach meal_suggestion on rich_context and
           also populate mealContent from the same payload — showing both is
           duplicate UI). Fall back to the legacy meal box only when there is
           no rich-context card. */}
              {hasMealContent && !hasRichContextEntries && (
                <div className="assistant-chat-suggested-blocks__meal-content-box">
                  {!!mealTitle && (
                    <Typography
                      variant="body2"
                      className="assistant-chat-suggested-blocks__meal-title"
                    >
                      {mealTitle}
                    </Typography>
                  )}
                  {!!macroFocus.length && (
                    <div className="assistant-chat-suggested-blocks__macro-row">
                      {macroFocus.slice(0, 4).map(([macro, value]) => (
                        <span key={macro} className="assistant-chat-suggested-blocks__macro-pill">
                          {`${macro}: ${value}`}
                        </span>
                      ))}
                    </div>
                  )}
                  {mealItems.slice(0, 4).map((item, idx) => {
                    const name = typeof item?.name === 'string' ? item.name.trim() : '';
                    if (!name) return null;
                    return (
                      <Typography
                        key={`${name}_${idx}`}
                        variant="subtitle2"
                        className="assistant-chat-suggested-blocks__meal-item"
                      >
                        • {name}
                      </Typography>
                    );
                  })}

                  {!!block.description && (
                    <>
                      <button
                        type="button"
                        className="assistant-chat-suggested-blocks__reason-toggle"
                        onClick={() => setIsReasonExpanded((prev) => !prev)}
                        disabled={isLoading || isProcessed}
                      >
                        <span>{t('task.ai.suggestedBlocks.reason')}</span>
                        <span>{isReasonExpanded ? '▴' : '▾'}</span>
                      </button>
                      {isReasonExpanded && (
                        <Typography
                          variant="caption"
                          className="assistant-chat-suggested-blocks__reason-text"
                        >
                          {block.description}
                        </Typography>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Non-meal explanation text (e.g. "Fixed routine scheduled after
           Routine 8 ends...") — meal items show their description via the
           reason-toggle inside the meal box above instead, so this only
           renders when there's no meal box to carry it. Suppressed when a
           rich-context card is present — that card carries the explanation
           itself (sleep/workout/energy summary etc.), so showing both would
           be redundant. */}
              {!hasMealContent && !hasRichContextEntries && !!block.description && (
                <DescriptionBox description={block.description} />
              )}

              {/* Rich-context card (sleep summary, workout plan, energy summary,
           etc.) attached directly to this suggested block — e.g. a
           "Replan my day" workout adjustment — rendered on the calendar
           card itself rather than as a separate chat bubble. */}
              {hasRichContextEntries && (
                <AssistantChatRichContextCard entries={richContextEntries} />
              )}

              {variant === 'screen' ? (
                <SuggestedBlockActionsRow
                  visibility={actionsVisibility}
                  acceptLabel={buildEventManageTitle(block)}
                  acceptDisabled={shouldShowCalendarSelector && !selectedCalendarId}
                  onAccept={() =>
                    handleClickEventBlock(
                      shouldShowCalendarSelector
                        ? { ...block, calendarId: selectedCalendarId }
                        : block,
                      selectedRecurrence
                    )
                  }
                  onDiscard={() => onDiscard?.(block.eventId)}
                />
              ) : (
                <AssistantChatSuggestedBlockCTA
                  title={buildEventManageTitle(block)}
                  loading={isLoading}
                  type={'calendar'}
                  onClick={() =>
                    handleClickEventBlock(
                      shouldShowCalendarSelector
                        ? { ...block, calendarId: selectedCalendarId }
                        : block,
                      selectedRecurrence
                    )
                  }
                  disabled={isProcessed || (shouldShowCalendarSelector && !selectedCalendarId)}
                  processed={isProcessed}
                />
              )}
            </>
          </Collapse>
        </div>
      );
    }
  )
);

export default AssistantChatSuggestedEventBlock;
